import { useState } from 'react';
import { FlaskConical, Loader2, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { useConfigStore } from '@/store/useConfigStore';
import { selectOCRInstance, callOCR, containsAnyKeyword } from '@/services/ocrService';
import { Button } from '@/components/ui/Button';
import { Input, Switch } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { TagInput, Tag } from '@/components/ui/Tag';
import type { OCRResult } from '@/types';

export function LowQualityFilterPage() {
    const { lowQualityConfig, updateLowQualityConfig, ocrInstances } = useConfigStore();

    const [testOpen, setTestOpen] = useState(false);

    const hasAvailableInstance = !!selectOCRInstance(ocrInstances);

    return (
        <div className="flex h-full flex-col">
            <div className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-4 py-3">
                <div>
                    <h1 className="text-base font-semibold text-zinc-900">低质量图片过滤</h1>
                    <p className="text-xs text-zinc-500">通过 OCR 识别详情图中的低质量内容并移除</p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTestOpen(true)}
                    disabled={!hasAvailableInstance}
                >
                    <FlaskConical size={14} />
                    测试
                </Button>
            </div>

            <div className="flex-1 overflow-auto p-4">
                <div className="mx-auto flex max-w-3xl flex-col gap-4">
                    {/* 说明 */}
                    <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                        <Info size={14} className="mt-0.5 shrink-0" />
                        <span>可选择过滤详情图、主图、SKU 图。若 SKU 图被删除且开启「删除空 SKU 行」，则整行移除。</span>
                    </div>

                    {/* 配置面板 */}
                    <section className="rounded-lg border border-zinc-200 bg-white p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-sm font-semibold text-zinc-900">配置</h2>
                                <p className="text-xs text-zinc-500">开启后可对图片执行低质量过滤</p>
                            </div>
                            <Switch
                                checked={lowQualityConfig.enabled}
                                onChange={(v) => updateLowQualityConfig({ enabled: v })}
                                label={lowQualityConfig.enabled ? '已启用' : '已禁用'}
                            />
                        </div>

                        {/* 过滤目标 */}
                        <div className="mt-4">
                            <p className="mb-2 text-xs font-medium text-zinc-600">过滤目标</p>
                            <div className="flex flex-wrap gap-3">
                                <label className="flex items-center gap-2 cursor-pointer rounded-md border border-zinc-200 px-3 py-1.5 transition-colors hover:bg-zinc-50">
                                    <input type="checkbox" checked={lowQualityConfig.filterDetailImages}
                                        onChange={(e) => updateLowQualityConfig({ filterDetailImages: e.target.checked })}
                                        className="h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500" />
                                    <span className="text-sm text-zinc-700">过滤详情图</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer rounded-md border border-zinc-200 px-3 py-1.5 transition-colors hover:bg-zinc-50">
                                    <input type="checkbox" checked={lowQualityConfig.filterMainImages}
                                        onChange={(e) => updateLowQualityConfig({ filterMainImages: e.target.checked })}
                                        className="h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500" />
                                    <span className="text-sm text-zinc-700">过滤主图</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer rounded-md border border-zinc-200 px-3 py-1.5 transition-colors hover:bg-zinc-50">
                                    <input type="checkbox" checked={lowQualityConfig.filterSkuImages}
                                        onChange={(e) => updateLowQualityConfig({ filterSkuImages: e.target.checked })}
                                        className="h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500" />
                                    <span className="text-sm text-zinc-700">过滤 SKU 图</span>
                                </label>
                            </div>
                            {lowQualityConfig.filterSkuImages && (
                                <label className="mt-2 flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={lowQualityConfig.removeSkuRowIfEmpty}
                                        onChange={(e) => updateLowQualityConfig({ removeSkuRowIfEmpty: e.target.checked })}
                                        className="h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500" />
                                    <span className="text-sm text-zinc-700">SKU 图被删除后，整个 SKU 行也删除</span>
                                </label>
                            )}
                        </div>

                        <div className="mt-4">
                            <TagInput
                                label="低质量关键词"
                                tags={lowQualityConfig.keywords}
                                onAdd={(tag) => updateLowQualityConfig({ keywords: [...lowQualityConfig.keywords, tag] })}
                                onRemove={(i) => updateLowQualityConfig({ keywords: lowQualityConfig.keywords.filter((_, idx) => idx !== i) })}
                                placeholder="输入关键词后回车添加"
                            />
                            <p className="mt-1 text-xs text-zinc-400">OCR 识别到的文字包含任一关键词时，该图片将被移除</p>
                        </div>
                    </section>
                </div>
            </div>

            <TestModal
                open={testOpen}
                onClose={() => setTestOpen(false)}
                keywords={lowQualityConfig.keywords}
            />
        </div>
    );
}

interface TestModalProps {
    open: boolean;
    onClose: () => void;
    keywords: string[];
}

function TestModal({ open, onClose, keywords }: TestModalProps) {
    const [imageUrl, setImageUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<OCRResult | null>(null);
    const [hits, setHits] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);

    const handleDetect = async () => {
        if (!imageUrl.trim()) return;
        const instance = selectOCRInstance(useConfigStore.getState().ocrInstances);
        if (!instance) {
            setError('无可用 OCR 实例，请先在 OCR 管理页面添加并启用实例');
            return;
        }
        setLoading(true);
        setError(null);
        setResult(null);
        setHits([]);
        try {
            const res = await callOCR(instance, imageUrl.trim());
            setResult(res);
            if (res.success && res.text) {
                setHits(containsAnyKeyword(res.text, keywords));
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : '识别失败');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setImageUrl('');
        setResult(null);
        setHits([]);
        setError(null);
        setLoading(false);
        onClose();
    };

    const isLowQuality = hits.length > 0;

    return (
        <Modal open={open} onClose={handleClose} title="低质量图片检测" width="lg">
            <div className="flex flex-col gap-4">
                <Input
                    label="图片 URL"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="输入要检测的图片链接"
                />
                <div className="flex justify-end">
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleDetect}
                        disabled={loading || !imageUrl.trim()}
                    >
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <FlaskConical size={14} />}
                        识别检测
                    </Button>
                </div>

                {(loading || result || error) && (
                    <div className="flex flex-col gap-3">
                        {error && (
                            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                                {error}
                            </div>
                        )}
                        {result && (
                            <>
                                <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
                                    <p className="mb-1.5 text-xs font-medium text-zinc-500">OCR 识别文字</p>
                                    <p className="whitespace-pre-wrap break-words text-sm text-zinc-800">
                                        {result.success ? (result.text || '（未识别到文字）') : `识别失败：${result.error}`}
                                    </p>
                                </div>
                                <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
                                    <p className="mb-1.5 text-xs font-medium text-zinc-500">判定结果</p>
                                    <div className="flex items-center gap-2">
                                        {isLowQuality ? (
                                            <>
                                                <AlertCircle size={16} className="text-red-500" />
                                                <span className="text-sm font-medium text-red-600">判定为低质量图片</span>
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle2 size={16} className="text-green-500" />
                                                <span className="text-sm font-medium text-green-600">非低质量图片</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
                                    <p className="mb-1.5 text-xs font-medium text-zinc-500">命中关键词</p>
                                    {hits.length > 0 ? (
                                        <div className="flex flex-wrap gap-1.5">
                                            {hits.map((kw, i) => (
                                                <Tag key={`${kw}-${i}`} label={kw} color="red" />
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-zinc-400">未命中任何关键词</p>
                                    )}
                                </div>
                            </>
                        )}
                        {loading && !result && !error && (
                            <div className="flex items-center gap-2 text-sm text-zinc-400">
                                <Loader2 size={14} className="animate-spin" />
                                正在识别中...
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
}
